// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { TimeRangeValue } from "./entities";
import { ChartType, ScoreLevel } from "./entities/base-metric.entity";

// ============================================================================
// Core Domain Interfaces
// ============================================================================

export type HealthScoreVariant = 'excellent' | 'healthy' | 'unsteady' | 'critical' | 'stable';


// Workspace interface
export interface Workspace {
  id: string;
  name: string;
  projectSlugs: string[];
}

// Request interfaces for workspace operations
export interface CreateWorkspaceRequest {
  name: string;
}

export interface UpdateWorkspaceRequest {
  id: string;
  name?: string;
}

// Project interface
export interface KeyProject {
  id: string;
  slug: string;
  name: string;
  description: string;
  logoUrl: string;
  healthScore: HealthScoreRawData;
  technicalInfluenceMetrics: {
    totalPoints: number;
    technicalInfluenceLevelName: string;
  };
  ecosystemInfluenceMetrics: {
    ecosystemInfluenceLevel: number;
    ecosystemInfluenceLevelName: string;
    totalPoints: number;
  };
  contributors: Contributor[];
  contributorCount: number;
  participants: Contributor[];
  participantCount: number;
  activity: ActivityData;
  isLfProject: boolean | undefined;
  activityCount: number;
}

export interface HealthScore {
  level: 'excellent' | 'healthy' | 'unsteady' | 'critical' | 'stable';
  color: string;
  backgroundColor: string;
}

/**
 * Raw health score data structure from backend API
 * Can contain overall score and detailed metric percentages
 */
export interface HealthScoreRawData {
  overallScore?: number;
  contributorPercentage?: number;
  popularityPercentage?: number;
  developmentPercentage?: number;
  securityPercentage?: number;
}

export interface InfluenceLevel {
  level: 'leading' | 'strong' | 'moderate' | 'emerging';
  color: string;
  iconClass: string;
}

export interface Contributor {
  id: string;
  name: string;
  avatarUrl: string;
}

export interface ActivityData {
  chartData: number[];
  changePercentage: number;
  trend: 'up' | 'down';
  period: string;
}

// ============================================================================
// Supporting Interfaces
// ============================================================================

export interface SummaryTableFilter {
  value: string;
  label: string;
}

export interface ViewState {
  mode: 'workspace-projects';
  selectedWorkspaceId?: string;
  selectedWorkspace?: Workspace;
}

export interface EmptyStateConfig {
  title: string;
  message: string;
  actionText?: string;
  showAction?: boolean;
  iconSrc?: string; // Custom image source
  iconClass?: string; // FontAwesome class fallback
  buttonIcon?: string; // Button icon class
}

// ============================================================================
// CDP API Interfaces
// ============================================================================

export interface ProjectDetailResponse {
  name: string;
  logo: string;
  description: string;
  firstCommit: string;
  softwareValue: number;
  healthScore: number;
  slug?: string;
}

// ============================================================================
// Column Component Interfaces
// ============================================================================

export interface ProjectInfo {
  name: string;
  logoUrl: string;
  slug: string;
  description: string;
  isLfProject?: boolean | undefined;
}

export interface HealthScoreDisplay {
  text: string;
  level: string;
  backgroundColor: string;
  color: string;
}

export interface InfluenceData {
  level: string;
  color: string;
}

export interface ContributorData {
  avatars: Contributor[];
  count: number;
}

export interface ParticipantData {
  avatars: Contributor[];
  count: number;
}

// ============================================================================
// Table Data Interface
// ============================================================================

export interface TableDataRow {
  id: string;
  projectInfo: ProjectInfo;
  healthScore: HealthScoreVariant;
  healthScoreRawData: HealthScoreRawData;
  technicalInfluence: string;
  ecosystemInfluence: string;
  technicalInfluencePoints: number;
  ecosystemInfluencePoints: number;
  contributors: ContributorData;
  participants: ParticipantData;
  activity: ActivityData;
}

// ============================================================================
// Influence Dashboard Interfaces
// ============================================================================

export type InfluenceType = 'technical' | 'ecosystem';
export type TechnicalMetricType = 'maintainers' | 'contributors' | 'commit_activities' |
  'pull_requests_opened' | 'avg_time_to_merge_prs';
export type EcosystemMetricType = 'board_members' | 'committee_members' | 'meeting_attendance' |
  'event_attendance' | 'event_speakers' | 'event_sponsorships' | 'meetup_attendance' |
  'collaboration_activity' | 'certified_individuals';
export type MetricType = TechnicalMetricType | EcosystemMetricType;

export interface ChartData {
  spanDate: string;
  year: number;
  month: number;
  day: number;
  metricCount: number;
  othersCount: number;
  totalCount: number;
}

export interface ChartDataWithMetricType {
  metricType: MetricType;
  chartData: ChartData[];
  firstDate: string;
  lastDate: string;
}

export interface InfluenceChartConfig {
  chartType: ChartType;
  lineType?: 'solid' | 'dashed' | 'dotted';
  lineColor?: string;
  description?: string;
}

export interface EcosystemInfluenceApiMetric {
  accountId: string;
  projectId: string;
  spanDate: string;
  year: number;
  month: number;
  day: number;
  timeRangeName: string;
  metricType: string;
  metricCount: number;
  othersCount: number;
  totalCount: number;
  projectTotalCount: number;
  projectAccountTotalCount: number;
}

export interface TechnicalInfluenceApiMetric {
  metricType: string;
  chartData: ChartData[];
  firstDate: string;
  lastDate: string;
}

export interface TechnicalMetricValues {
  maintainersCount: number;
  maintainersLevel?: string;
  contributorsPercent: string;
  contributorsLevel?: string;
  commitsPercent: string;
  commitsLevel?: string;
  prsOpenedPercent: string;
  prsOpenedLevel?: string;
  averageMergedTime: {
    percent: string | null;
    speed: 'faster' | 'slower' | 'same' | null;
  };
  avgMergeTimeLevel?: string;
}

export interface EcosystemMetricValues {
  // Percentage fields
  boardMembersCount: number;
  committeeMembersPercent: string | null;
  meetingAttendancePercent: string | null;
  eventAttendancePercent: string | null;
  eventSpeakersPercent: string | null;
  sponsorshipEventsPercent: string | null;
  meetupAttendancePercent: string | null;
  collaborationActivityPercent: string | null;
  tncCertificatePercent: string | null;
  // All-time total fields - indicates if project has ever used LFX tools for this activity
  // When = 0, project has never used LFX tools (uses non-LFX tools)
  // When > 0, project has used LFX tools at some point
  allTimeTotalBoardMembers: number;
  allTimeTotalCommitteeMembers: number;
  allTimeTotalMeetingAttendance: number;
  allTimeTotalEventAttendance: number;
  allTimeTotalMeetupAttendance: number;
  allTimeTotalEventSpeakers: number;
  allTimeTotalTncCertificate: number;
  allTimeTotalSponsorshipEvents: number;
  allTimeTotalCollaborationActivity: number;
  // Pre-calculated points from Snowflake (variable ranges - total: 0-316)
  boardMembersPoints: number;
  committeeMembersPoints: number;
  meetingAttendancePoints: number;
  eventAttendancePoints: number;
  eventSpeakersPoints: number;
  sponsorshipEventsPoints: number;
  meetupAttendancePoints: number;
  collaborationActivityPoints: number;
  tncCertificatePoints: number;
  // Individual metric level names (for UI detail page color mapping)
  collaborationActivityLevel: string;
  meetingAttendanceLevel: string;
  membershipTierLevel: string;
  boardMembersLevel: string;
  committeeMembersLevel: string;
  eventAttendanceLevel: string;
  eventSpeakersLevel: string;
  sponsorshipEventsLevel: string;
  meetupAttendanceLevel: string;
  tncCertificateLevel: string;
  // Pre-calculated influence level from Snowflake
  nonLfToolsCount: number;
  totalPoints: number;
  influenceLevel: number;
  influenceLevelName: string;
}

export interface InfluenceApiResponse {
  accountId: string;
  projectId: string;
  projectSlug: string;
  projectName?: string | null;
  projectType?: string | null;
  foundationName?: string | null;
  timeRangeName?: TimeRangeValue;
  timeRangeType?: TimeRangeValue;
  metrics: EcosystemInfluenceApiMetric[] | TechnicalInfluenceApiMetric[];
  technicalInfluenceScore?: TechnicalInfluenceScoreData;
  ecosystemInfluenceScore?: EcosystemInfluenceScoreData;
  metricValues?: TechnicalMetricValues | EcosystemMetricValues;
  technicalInfluenceScoreValue?: string;
  technicalInfluenceScoreLevelName?: ScoreLevel;
}

export interface TechnicalInfluenceScoreData {
  totalScore: number;
  influenceLevel: 'emerging' | 'growing' | 'strong' | 'dominant';
  maintainersScore: number;
  contributorsScore: number;
  commitsScore: number;
  prsScore: number;
  mergeTimeScore: number;
}

export interface EcosystemInfluenceScoreData {
  level: number; // 1-4 from Snowflake
  category: string; // Description from Snowflake
  rawScore: number; // Raw score if available
  // Individual metric scores (0-3 each)
  membershipScore: number;
  boardMemberScore: number;
  committeeMemberScore: number;
  meetingHoursScore: number;
  eventAttendanceScore: number;
  meetupAttendanceScore: number;
  eventSpeakersScore: number;
  tncCertificateScore: number;
  sponsorshipScore: number;
  collaborationActivityScore: number;
  isLfProject: boolean | undefined;
  meetingHoursCount?: number;
  totalMeetingHours?: number;
  meetingHoursPercentage?: number;
}

export type MetricCardStatusType = 'success' | 'warning' | 'danger' | 'info' | 'none';

// ============================================================================
// Metric Configuration Interfaces
// ============================================================================

export interface MetricDisplayConfig {
  displayName: string;
  titleDescription: string;
}

export interface MetricChartConfig extends InfluenceChartConfig {
  // Additional chart configuration properties can be added here in the future
  customProperty?: string;
}

export interface MetricTableColumn {
  name: string;
  prop: string;
}

export interface MetricOffcanvasConfig {
  tableColumns?: MetricTableColumn[];
}

export interface MetricBottomSummary {
  title: string;
  description: string;
  icon: string;
  iconColor: string;
  borderColor: string;
}

export interface CompleteMetricConfig {
  display: MetricDisplayConfig;
  chart: MetricChartConfig;
  offcanvas?: MetricOffcanvasConfig;
}

// ============================================================================
// Metric Type Utilities
// ============================================================================

/**
 * Template identifier constants for type-safe template references
 *
 * These constants provide shared template identifiers that can be used across
 * entities and components without creating circular dependencies.
 */
export const TEMPLATE_IDENTIFIERS = {
  USER_PROFILE: 'userProfileTemplate'
} as const;

/**
 * Type for template identifier values
 */
export type TemplateIdentifier = typeof TEMPLATE_IDENTIFIERS[keyof typeof TEMPLATE_IDENTIFIERS];

/**
 * Get all ecosystem metric types
 */
export function getEcosystemMetricTypes(): readonly EcosystemMetricType[] {
  return [
    'board_members',
    'committee_members',
    'meeting_attendance',
    'event_attendance',
    'event_speakers',
    'event_sponsorships',
    'meetup_attendance',
    'collaboration_activity',
    'certified_individuals'
  ] as const;
}

